import React, { useState, useEffect } from "react";
import { useStepNavigate } from "../hooks/useStepNavigate";
import { useOrder } from "../context/OrderContext";
import { useOrderSave } from "../context/OrderSaveContext";
import TablerLayout from "../components/layout/TablerLayout";
import StepperNavButton from "../components/stepper/StepperNavButton";
import { showErrorToast, showSuccessToast } from "../components/toasts/ToastService";

const orrMerevitoTipusok = [
    { value: "Normál", label: "Normál anyagú és normál formájú" },
    { value: "Puha, kifli", label: "Puha anyagú és kifli formájú" },
];

const selectionMap = {
    "Normál": { type: "Normál formájú", mater: "Normál anyagú" },
    "Puha, kifli": { type: "Kifli formájú", mater: "Puha anyagú" },
    "": { type: "", mater: "" },
};

const getSelectionFromOrder = (type, mater) => {
    const entry = Object.entries(selectionMap).find(
        ([, mapped]) => mapped.type === type && mapped.mater === mater
    );
    if (entry) return entry[0];
    if (!type && !mater) return "";
    return null;
};


const OrrMerevito = () => {
    const { order, updateOrder } = useOrder();
    const { saveOrder } = useOrderSave();
    const navigate = useStepNavigate();

    const shoesMedLeft = order.hmeliorder?.hmeliorder_shoes_med_left || "";
    const shoesMedRight = order.hmeliorder?.hmeliorder_shoes_med_right || "";
    const isDifferentShoes = shoesMedLeft !== shoesMedRight;

    const modellCode = (order.hmeliorder?.hmeliorder_modell_num ?? "").toString().trim();
    const isNoseBraceForbidden = modellCode.endsWith("3");

    const isSelectionValid = (value) =>
    isNoseBraceForbidden ? value === "" : Boolean(value);


    const [selectedLeft, setSelectedLeft] = useState(() => {
        const initial = getSelectionFromOrder(
            order.hmeliorder?.hmeliorder_nose_brace_type_left,
            order.hmeliorder?.hmeliorder_nose_brace_mater_left
        );
        return isNoseBraceForbidden ? "" : initial ?? "";
    });
    const [selectedRight, setSelectedRight] = useState(() => {
        const initial = getSelectionFromOrder(
            order.hmeliorder?.hmeliorder_nose_brace_type_right,
            order.hmeliorder?.hmeliorder_nose_brace_mater_right
        );
        return isNoseBraceForbidden ? "" : initial ?? "";
    });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!isNoseBraceForbidden) return;
        if (selectedLeft === "" && selectedRight === "") return;
        setSelectedLeft("");
        setSelectedRight("");
    }, [isNoseBraceForbidden, selectedLeft, selectedRight]);

    const handleSelect = (side, value) => {
        if (isNoseBraceForbidden && value !== "") return;

        const { type, mater } = selectionMap[value] || selectionMap[""];
        if (!isDifferentShoes) {
            setSelectedLeft(value);
            setSelectedRight(value);
            updateOrder({
                ...order,
                hmeliorder: {
                    ...order.hmeliorder,
                    hmeliorder_nose_brace_mater_left: mater,
                    hmeliorder_nose_brace_type_left: type,
                    hmeliorder_nose_brace_mater_right: mater,
                    hmeliorder_nose_brace_type_right: type,
                },
            });
            return;
        }
        if (side === "left") {
            setSelectedLeft(value);
            updateOrder({
                ...order,
                hmeliorder: {
                    ...order.hmeliorder,
                    hmeliorder_nose_brace_mater_left: mater,
                    hmeliorder_nose_brace_type_left: type,
                },
            });
        } else if (side === "right") {
            setSelectedRight(value);
            updateOrder({
                ...order,
                hmeliorder: {
                    ...order.hmeliorder,
                    hmeliorder_nose_brace_mater_right: mater,
                    hmeliorder_nose_brace_type_right: type,
                },
            });
        }
    };

    const canProceed = isDifferentShoes
        ? isSelectionValid(selectedLeft) && isSelectionValid(selectedRight)
        : isSelectionValid(selectedLeft);

    const handleNext = async () => {
        if (!canProceed) {
            showErrorToast("Kérem, válasszon orr merevítőt vagy válassza a 'Nem kell' opciót!");
            return;
        }

        const leftKey = selectedLeft ?? "";
        const rightKey = isDifferentShoes ? selectedRight ?? "" : leftKey;
        const leftSelection = selectionMap[leftKey] || selectionMap[""];
        const rightSelection = selectionMap[rightKey] || selectionMap[""];

        const updatedOrder = {
            ...order,
            hmeliorder: {
                ...order.hmeliorder,
                hmeliorder_nose_brace_type_left: leftSelection.type,
                hmeliorder_nose_brace_mater_left: leftSelection.mater,
                hmeliorder_nose_brace_type_right: rightSelection.type,
                hmeliorder_nose_brace_mater_right: rightSelection.mater,
            },
        };

        try {
            setLoading(true);
            updateOrder(updatedOrder);
            const result = await saveOrder(updatedOrder);

            if (result.success) {
                showSuccessToast("A rendelés sikeresen mentve!");
                navigate("/Donga");
            } else {
                showErrorToast("Hiba történt a rendelés mentésekor!");
            }
        } catch (error) {
            console.error("Orr merevítő mentési hiba:", error);
            showErrorToast("Hiba történt a rendelés mentésekor!");
        } finally {
            setLoading(false);
        }
    };

    const renderOrrMerevitoOptions = (side, selectedValue) => (
        <div className="mb-6 m-0">
            <div className="d-flex flex-column gap-3 align-items-center">
                {orrMerevitoTipusok.map((tipus) => (
                    <button
                        key={tipus.value}
                        type="button"
                        disabled={isNoseBraceForbidden && tipus.value !== ""}
                        className={`btn w-100 py-4 px-3 shadow-sm rounded-4 d-flex flex-column align-items-center fw-bold
                            ${selectedValue === tipus.value
                                ? "btn-primary text-white"
                                : "btn-outline-primary text-primary border-primary"
                            }`}
                        style={{
                            fontSize: 20,
                            fontWeight: 600,
                            borderWidth: 2,
                            minHeight: 100,
                            minWidth: 500
                        }}
                        onClick={() => handleSelect(side, tipus.value)}
                    >
                        <span>{tipus.label}</span>
                    </button>
                ))}
            </div>
        </div>
    );

    return (
        <TablerLayout title="Válassza ki az orr merevítőt!">
            <div className="row justify-content-center">
                <div className="col-md-12">
                    <div className="card-body">
                        {/* {isDifferentShoes ? (
                            <div className="row-md-6 d-flex justify-content-center gap-4">
                                <div className="col-md-6">
                                    <h6 className="fw-bold mb-3 text-center">Bal cipő</h6>
                                    {renderOrrMerevitoOptions("left", selectedLeft)}
                                </div>
                                <div className="col-md-6">
                                    <h6 className="fw-bold mb-3 text-center">Jobb cipő</h6>
                                    {renderOrrMerevitoOptions("right", selectedRight)}
                                </div>
                            </div>
                        ) : ( 
                            <> */}
                                <div className="col-md-12 d-flex justify-content-center gap-4">
                                    {renderOrrMerevitoOptions("left", selectedLeft, selectedRight)}
                                </div>
                            {/* </>
                        )} */}
                    </div>
                    <div className="card-footer d-flex justify-content-between">
                        <StepperNavButton
                            side="left"
                            label="Vissza"
                            onClick={() => navigate("/KeregTipus")}
                        />
                        <StepperNavButton
                            side="right"
                            label="Tovább"
                            onClick={handleNext}
                            disabled={!canProceed || loading}
                        />
                    </div>
                </div>
            </div>
        </TablerLayout>
    );
};

export default OrrMerevito;
